/* eslint-disable jsx-a11y/anchor-is-valid */
import './ScenarioChatPanel.css';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, FloatButton, Form, Input, message, Modal, Select, Spin, Tag, Tooltip } from 'antd';
import ChatBubble from '../@shared/ChatBubble';
import ChatTextArea from '../@shared/ChatTextArea';
import ScenarioChatContext from '../../contexts/data/ScenarioChatContext';
import TextArea from 'antd/es/input/TextArea';
import { FormContainer } from '@leedpulse-monorepo/components';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import { MessagePayload, SendMessagePayload } from '../../../domain/use_cases/scenario_chat/sendMessageToAI';
import { JobType, jobTypeAsString, ContextStatus } from "@leedpulse-monorepo/enums";
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import Scenario from '../../../domain/models/Scenario';
import { ESmsRole } from '@leedpulse-monorepo/enums';
import { BellOutlined, CalendarOutlined, InfoCircleOutlined, ReloadOutlined, SignatureOutlined } from '@ant-design/icons';
import { getTagColor } from '../../../core/utils/commonUtils';
import { renderContactQualificationAiQualificationOption } from '../../../domain/enums/contact/contact_qualification_ai/ContactQualificationAiQualification';
import { generateRandomContext, generateRandomPerson } from '@leedpulse-monorepo/utils';

interface ScenarioChatPanelProps {
  scenario: Scenario;
}

const ScenarioChatPanel: React.FC<ScenarioChatPanelProps> = ({
  scenario
}) => {
  const scenarioChatContext = useContext(ScenarioChatContext);
  const subscriptionsContext = useContext(SubscriptionsContext);
  const authContext = useContext(AuthenticationContext);

  const messagesRef = useRef<HTMLDivElement>(null);

  const [firstname, setFirstname] = useState(() => generateRandomPerson().firstName);
  const [lastname, setLastname] = useState(() => generateRandomPerson().lastName);
  const [apiKeySubscriptionSelected, setApiKeySubscriptionSelected] = useState('');
  const [clientContext, setClientContext] = useState(() => generateRandomContext());
  const [firstnameInputStatus, setFirstnameInputStatus] = useState<'' | "error">('');
  const [lastnameInputStatus, setLastnameInputStatus] = useState<'' | "error">('');

  useEffect(() => {
    return () => {
      scenarioChatContext.reset();
      
    }
    
  }, [])

  useEffect(() => {
    if (messagesRef.current && scenarioChatContext.status === ContextStatus.SUCCESS) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [scenarioChatContext.status]);

  useEffect(() => {
    if (messagesRef.current && scenarioChatContext.items.length > 0) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [scenarioChatContext.items]);

  const sendMessage = (responseType: JobType, message?: string) => {
    const scenarioId = scenario.id;
    const authentication = authContext.authentication;
    if (authentication && scenarioId) {
      const payload: SendMessagePayload = {
        sResponseType: responseType,
        oContact: {
          id_api_key: apiKeySubscriptionSelected,
          contact: {
            firstname,
            lastname,
            owner: authentication.id,
            prospect_category: scenarioId,
            details: clientContext,
            civility: 'M_MME',
            sender: '33600000000'
          }
        }
      }
      if (responseType !== JobType.firstContact) {
        payload.aSMS = scenarioChatContext.items;
        payload.sUserText = message || '';
      }
      scenarioChatContext.sendMessage(payload);
    }
  }

  const handleSendMessageClickBtn = (message: string) => {
    sendMessage(JobType.reply, message);
  }

  const handleStartConversationClickBtn = () => {
    if (!firstname || !lastname || !apiKeySubscriptionSelected) {
      return message.error('Veuillez remplir tous les champs');
    }

    sendMessage(JobType.firstContact);
  }

  const handleResetClickBtn = () => {
    setClientContext(() => generateRandomContext());
    setFirstname(() => generateRandomPerson().firstName);
    setLastname(() => generateRandomPerson().lastName);
    scenarioChatContext.reset();
  }

  const handleFirstnameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstname(e.target.value.trim());
    if (e.target.value.length === 0) {
      setFirstnameInputStatus('error');
    } else {
      setFirstnameInputStatus('');
    }
  };

  const handleLastnameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastname(e.target.value.trim());
    if (e.target.value.length === 0) {
      setLastnameInputStatus('error');
    } else {
      setLastnameInputStatus('');
    }
  };

  const originalPromptModal = () => {
    Modal.info({
      title: 'Informations des prompts de l\'IA',
      width: 800,
      maskClosable: true,
      content: (
        <div>
          {scenarioChatContext.originalPrompts.map((prompt, index) => {
            return (
              <div key={index}>
                <p><b>{prompt.role} : </b></p>
                <p style={{ whiteSpace: "pre-line" }}>{prompt.content}</p>
              </div>
            )
          })}
        </div>
      ),
    })
  };

  const renderBubbleAction = (message: MessagePayload): ReactNode[] => {

    const bubbleActions = [];
    const qualificationMessage = renderContactQualificationAiQualificationOption(message.qualification);
    if (message.role === ESmsRole.assistant && qualificationMessage) {
      bubbleActions.push(
        <Tooltip title="Qualification de l'IA">
          <Tag color={getTagColor(message.qualification)} style={{ textAlign: 'center', borderRadius: '1.5rem', margin: '0' }}>
            {qualificationMessage}
          </Tag>
        </Tooltip>
      )
    }

    bubbleActions.push(
      <Tooltip title="Prompts de l'IA">
        <Button
          type="text"
          icon={<InfoCircleOutlined />}
          onClick={originalPromptModal} />
      </Tooltip>
    )

    return bubbleActions;
  };

  const isChatLoadingInitialState = scenarioChatContext.status === ContextStatus.LOADING
    && scenarioChatContext.items.length === 0

  return (
    <div
      className="scenario-chat-panel"
    >
      <div className='scenario-chat-panel__header'>
        <h2>Demo</h2>
        <ConfigProvider
          theme={{
            token: {
              controlHeight: 32
            },
            components: {
              Button: {
                fontWeight: 500,
                paddingContentHorizontal: 16
              }
            }
          }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleResetClickBtn}>
            Réinitialiser
          </Button>
        </ConfigProvider>
      </div>
      <div className="scenario-chat-panel__messages" ref={messagesRef}>
        {isChatLoadingInitialState ? (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin />
          </div>
        ) : (
          scenarioChatContext.items.length === 0 ?
            <div className='scenario-chat-panel__form'>
              <Form layout='vertical'>
                <FormContainer oneColumnMax>
                  <Form.Item label='Abonnement'>
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        option?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      placeholder="Sélectionner..."
                      onChange={(value) => setApiKeySubscriptionSelected(value)}
                    >
                      {
                        subscriptionsContext.items.map((sub) => (
                          <Select.Option key={sub.id} value={sub.apiKey}>
                            {sub.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </FormContainer>
                <FormContainer twoColumnsMax>
                  <Form.Item label='Prénom'>
                    <Input placeholder='Jean'
                      status={firstnameInputStatus}
                      value={firstname}
                      onChange={(e) => handleFirstnameChanged(e)} />
                  </Form.Item>

                  <Form.Item label='Nom'>
                    <Input placeholder='Dupont'
                      status={lastnameInputStatus}                      
                      value={lastname}
                      onChange={(e) => handleLastnameChanged(e)} />
                  </Form.Item>
                </FormContainer>
                <FormContainer oneColumnMax>
                  <Form.Item label='Contexte client'>
                    <TextArea
                      rows={3}
                      placeholder="Recherche maison à Strasbourg, proche gare et centre ville"
                      onChange={(e) => setClientContext(e.target.value)}
                      value={clientContext}
                      style={{
                        resize: 'none',
                        width: '100%'
                      }} />
                  </Form.Item>
                </FormContainer>
              </Form>
              <Button
                type="primary"
                disabled={apiKeySubscriptionSelected && firstname && lastname ? false : true}
                onClick={handleStartConversationClickBtn}>
                Démarrer la conversation
              </Button>
            </div>
            :
            <>
              {scenarioChatContext.items
                .map((message, index) => {
                  return (
                    <ChatBubble
                      key={index}
                      message={message.message}
                      date={new Date(message.created).toLocaleString()}
                      role={message.role}
                      manualSending={message.role === ESmsRole.user}
                      actions={renderBubbleAction(message)} />
                  );
                })}
              <FloatButton.Group
                type='primary'
                trigger="click"
                shape="square"
                tooltip="Simuler une action de l'IA"
                style={{ insetInlineEnd: 55, marginBottom: 73 }}
                icon={<SignatureOutlined />}
              >
                <FloatButton icon={<BellOutlined />} description="48h" tooltip={jobTypeAsString(JobType.reminderNoresponse)} onClick={() => sendMessage(JobType.reminderNoresponse)} />
                <FloatButton icon={<CalendarOutlined />} description="30J" tooltip={jobTypeAsString(JobType.followUp30Days)} onClick={() => sendMessage(JobType.followUp30Days)} />
                <FloatButton icon={<CalendarOutlined />} description="90J" tooltip={jobTypeAsString(JobType.followUp90Days)} onClick={() => sendMessage(JobType.followUp90Days)} />
                <FloatButton icon={<CalendarOutlined />} description="180J" tooltip={jobTypeAsString(JobType.followUp180Days)} onClick={() => sendMessage(JobType.followUp180Days)} />
                <FloatButton icon={<CalendarOutlined />} description="365J" tooltip={jobTypeAsString(JobType.followUp365Days)} onClick={() => sendMessage(JobType.followUp365Days)} />
              </FloatButton.Group>
            </>
        )}
      </div>
      {
        scenarioChatContext.items.length === 0 ?
          null
          :
          <ChatTextArea
            placeholder='Ecrire un message'
            loading={scenarioChatContext.status === ContextStatus.LOADING}
            onMessageSent={handleSendMessageClickBtn} />
      }
    </div>
  );
};

export default ScenarioChatPanel;
